import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import layout from '../layout/layout';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import {
  resetUpdateCoachProfile,
  updateCoachProfile,
} from '../Store/StudentSlices/updateCoachProfileSlice';
import { getCoachInfo } from '../Store/StudentSlices/getCoachSlice';
import {
  resetProfileImageUpload,
  uploadProfileImage,
} from '../Store/StudentSlices/uploadProfileImageSlice';
import {
  getGoogleIntegrationAuthUrl,
  resetGoogleIntegrationAuth,
} from '../Store/StudentSlices/googleIntegrationAuthSlice';
import {
  getNotificationSettings,
  updateNotificationSettings,
} from '../Store/StudentSlices/notificationSettingsSlice';
import {
  getLocationSearchResults,
  resetLocationSearch,
} from '../Store/StudentSlices/locationSearchSlice';
import {
  createSupportTicket,
  resetSupportTicket,
} from '../Store/StudentSlices/supportTicketSlice';
import {
  deleteAccountSession,
  deleteAllAccountSessions,
  getAccountSessions,
  resetAccountSessions,
} from '../Store/StudentSlices/accountSessionsSlice';
import {
  resetSignIn,
} from '../Store/StudentSlices/signInSlice';
import {
  changePassword,
  resetChangePassword,
} from '../Store/StudentSlices/changePasswordSlice';
import {
  getIntegrations,
  resetIntegrations,
} from '../Store/StudentSlices/integrationStatusSlice';
import TabButton from '../Component/Settings/TabButton';
import ProfileSection from '../Component/Settings/ProfileSection';
import AccountSection from '../Component/Settings/AccountSection';
import NotificationsSection from '../Component/Settings/NotificationsSection';
import IntegrationsSection from '../Component/Settings/IntegrationsSection';
import HelpSupportSection from '../Component/Settings/HelpSupportSection';
import PlaceholderSection from '../Component/Settings/PlaceholderSection';
import UploadModal from '../Component/Settings/UploadModal';
import PasswordModal from '../Component/Settings/PasswordModal';
import SettingsOverlays from '../Component/Settings/SettingsOverlays';
import {
  SETTINGS_TABS,
  type IntegrationKey,
  type NotificationKey,
  type ProfileRow,
  type TabKey,
} from '../Component/Settings/types';
import {
  appNotificationOptions,
  emailNotificationOptions,
  integrationOptions,
  supportCategoryOptions,
  supportEmail,
  supportFaqs,
  supportCategories,
} from '../Component/Settings/constants';
import {
  formatDisplayValue,
  getInitials,
  getStringValue,
  getTabSlug,
  splitFullName,
} from '../Component/Settings/utils';
import { redirectToLogin } from '../utils/authCleanup';

type ProfileState = {
  firstNameDraft: string;
  lastNameDraft: string;
  isEditingName: boolean;
  bioDraft: string;
  isEditingBio: boolean;
  locationDraft: string;
  isEditingLocation: boolean;
};

type UploadState = {
  isOpen: boolean;
  file: File | null;
  error: string;
  previewUrl: string;
  zoom: number;
  isDragging: boolean;
  dragOffset: { x: number; y: number };
  baseImageSize: { width: number; height: number };
};

type PasswordState = {
  isOpen: boolean;
  step: 'current' | 'reset';
  password: string;
  newPassword: string;
  confirmPassword: string;
  showPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
};

type NotificationConfirm = {
  type: 'email' | 'app';
  nextValue: boolean;
};

type NotificationState = {
  settings: Record<NotificationKey, boolean>;
  emailEnabled: boolean;
  appEnabled: boolean;
  confirmToggle: NotificationConfirm | null;
  snackbarOpen: boolean;
};

type IntegrationConfirm = {
  key: IntegrationKey;
  nextValue: boolean;
};

type IntegrationState = {
  settings: Record<IntegrationKey, boolean>;
  confirmToggle: IntegrationConfirm | null;
};

type SupportState = {
  isQueryOpen: boolean;
  category: string;
  query: string;
  openFaqIndex: number | null;
  isEmailCopied: boolean;
  isSuccessOpen: boolean;
};

const Settings = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>('Profile');
  const [profileState, setProfileState] = useState<ProfileState>({
    firstNameDraft: '',
    lastNameDraft: '',
    isEditingName: false,
    bioDraft: '',
    isEditingBio: false,
    locationDraft: '',
    isEditingLocation: false,
  });
  const [uploadState, setUploadState] = useState<UploadState>({
    isOpen: false,
    file: null,
    error: '',
    previewUrl: '',
    zoom: 1,
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    baseImageSize: { width: 0, height: 0 },
  });
  const [passwordState, setPasswordState] = useState<PasswordState>({
    isOpen: false,
    step: 'current',
    password: '',
    newPassword: '',
    confirmPassword: '',
    showPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });
  const [notificationState, setNotificationState] = useState<NotificationState>({
    settings: {
      emailMatchAlert: true,
      emailUpcomingSessionReminder: true,
      emailSessionCancellation: true,
      emailReschedulingRequest: true,
      emailPlatformAnnouncement: true,
      emailNewMessage: true,
      inAppUpcomingSessionReminder: true,
      inAppSessionChanges: true,
      inAppNewStudentAssigned: true,
      inAppNewMessage: true,
      inAppActionRequired: true,
      inAppAttendanceUpdate: true,
      inAppPlatformUpdate: true,
    },
    emailEnabled: true,
    appEnabled: true,
    confirmToggle: null,
    snackbarOpen: false,
  });
  const [integrationState, setIntegrationState] = useState<IntegrationState>({
    settings: {
      googleCalendar: false,
      outlookCalendar: false,
      zoomCalendar: true,
      appleCalendar: false,
    },
    confirmToggle: null,
  });
  const [supportState, setSupportState] = useState<SupportState>({
    isQueryOpen: false,
    category: supportCategories[0],
    query: '',
    openFaqIndex: null,
    isEmailCopied: false,
    isSuccessOpen: false,
  });
  const updateProfileState = (patch: Partial<ProfileState>) => {
    setProfileState((prev) => ({ ...prev, ...patch }));
  };
  const updateUploadState = (patch: Partial<UploadState>) => {
    setUploadState((prev) => ({ ...prev, ...patch }));
  };
  const updatePasswordState = (patch: Partial<PasswordState>) => {
    setPasswordState((prev) => ({ ...prev, ...patch }));
  };
  const updateNotificationState = (patch: Partial<NotificationState>) => {
    setNotificationState((prev) => ({ ...prev, ...patch }));
  };
  const updateIntegrationState = (patch: Partial<IntegrationState>) => {
    setIntegrationState((prev) => ({ ...prev, ...patch }));
  };
  const updateSupportState = (patch: Partial<SupportState>) => {
    setSupportState((prev) => ({ ...prev, ...patch }));
  };
  const {
    firstNameDraft,
    lastNameDraft,
    isEditingName,
    bioDraft,
    isEditingBio,
    locationDraft,
    isEditingLocation,
  } = profileState;
  const {
    isOpen: isUploadOpen,
    file: uploadFile,
    error: uploadError,
    previewUrl,
    zoom,
    isDragging: isDraggingImage,
    dragOffset,
    baseImageSize,
  } = uploadState;
  const {
    isOpen: isPasswordModalOpen,
    step: passwordStep,
    password: passwordDraft,
    newPassword: newPasswordDraft,
    confirmPassword: confirmPasswordDraft,
    showPassword,
    showNewPassword,
    showConfirmPassword,
  } = passwordState;
  const {
    settings: notificationSettings,
    emailEnabled: emailNotificationsEnabled,
    appEnabled: appNotificationsEnabled,
    confirmToggle,
    snackbarOpen,
  } = notificationState;
  const {
    settings: integrationSettings,
    confirmToggle: confirmIntegrationToggle,
  } = integrationState;
  const {
    isQueryOpen: isSupportQueryOpen,
    category: supportCategory,
    query: supportQuery,
    openFaqIndex,
    isEmailCopied,
    isSuccessOpen: isSupportSuccessOpen,
  } = supportState;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);
  const copyResetTimeoutRef = useRef<number | null>(null);
  const {
    loading: isProfileUpdating,
    error: hasProfileError,
    errorMessage: profileErrorMessage,
  } = useAppSelector((state) => state.updateCoachProfile);
  const {
    loading: isProfileImageUploading,
    error: hasProfileImageUploadError,
    errorMessage: profileImageUploadErrorMessage,
  } = useAppSelector((state) => state.uploadProfileImage);
  const {
    loading: isPasswordChanging,
    errorMessage: passwordChangeErrorMessage,
  } = useAppSelector((state) => state.changePassword);
  const {
    loading: isNotificationSettingsLoading,
    saving: isNotificationSettingsSaving,
    error: hasNotificationSettingsError,
    errorMessage: notificationSettingsErrorMessage,
    data: notificationSettingsData,
  } = useAppSelector((state) => state.notificationSettings);
  const { loading: isLocationSearching, results: locationResults } =
    useAppSelector((state) => state.locationSearch);
  const {
    loading: isSupportTicketLoading,
    error: hasSupportTicketError,
    errorMessage: supportTicketErrorMessage,
  } = useAppSelector((state) => state.supportTicket);
  const {
    loading: isAccountSessionsLoading,
    data: accountSessionsData,
    deletingIds: accountSessionDeletingIds,
    deletingAll: isAccountSessionsDeletingAll,
  } = useAppSelector((state) => state.accountSessions);
  const {
    loading: isGoogleAuthLoading,
    error: hasGoogleAuthError,
    errorMessage: googleAuthErrorMessage,
  } = useAppSelector((state) => state.googleIntegrationAuth);
  const {
    data: integrationsData,
  } = useAppSelector((state) => state.integrationStatus);
  const [microsoftAuthLoading, setMicrosoftAuthLoading] = useState(false);
  const [microsoftAuthError, setMicrosoftAuthError] = useState<string | null>(null);
  const [zoomAuthLoading, setZoomAuthLoading] = useState(false);
  const [zoomAuthError, setZoomAuthError] = useState<string | null>(null);
  const isZoomAuthLoading = zoomAuthLoading;
  const zoomAuthErrorMessage = zoomAuthError || '';
  const { coachInfo } = useAppSelector((state) => state.getCoach);

  const coach = coachInfo as any;
  const coachName =
    getStringValue(coach?.name) ||
    getStringValue(coach?.full_name) ||
    [getStringValue(coach?.first_name), getStringValue(coach?.last_name)]
      .filter(Boolean)
      .join(' ');
  const coachBio = getStringValue(coach?.bio);
  const coachLocation = getStringValue(coach?.location);
  const coachTimezone = getStringValue(coach?.timezone);
  const localTimezone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : '';
  const coachEmail =
    getStringValue(coach?.email) || getStringValue(coach?.user?.email);
  const coachAvatarUrl =
    getStringValue(coach?.avatar) ||
    getStringValue(coach?.avatar_url) ||
    getStringValue(coach?.profile_picture) ||
    getStringValue(coach?.profile_picture_url) ||
    getStringValue(coach?.profile_image) ||
    getStringValue(coach?.photo_url);
  const registeredEmailDisplay = formatDisplayValue(coachEmail);
  const fullNameDisplay = formatDisplayValue(coachName);
  const bioDisplay = formatDisplayValue(coachBio);
  const locationDisplay = formatDisplayValue(coachLocation);
  const avatarInitials = getInitials(coachName, coachEmail);
  const profileRows: ProfileRow[] = [
    {
      label: 'Timezone',
      value: formatDisplayValue(coachTimezone || localTimezone),
    },
    {
      label: 'Registered Email',
      value: registeredEmailDisplay,
    },
  ];

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', getTabSlug(tab));
    setSearchParams(nextParams, { replace: true });
  };

  const closeUpload = () => {
    updateUploadState({
      isOpen: false,
      file: null,
      error: '',
      zoom: 1,
      dragOffset: { x: 0, y: 0 },
    });
    dispatch(resetProfileImageUpload());
  };

  const openUpload = () => {
    dispatch(resetProfileImageUpload());
    dispatch(resetUpdateCoachProfile());
    updateUploadState({ isOpen: true });
  };

  const handleSavePhoto = async () => {
    if (!uploadFile || isProfileImageUploading || isProfileUpdating) {
      return;
    }
    try {
      const uploadResult = await dispatch(
        uploadProfileImage({ file: uploadFile })
      ).unwrap();
      const uploadedUrl = uploadResult.fileUrl;
      await dispatch(updateCoachProfile({ avatar: uploadedUrl })).unwrap();
      dispatch(getCoachInfo());
      closeUpload();
    } catch {
      // Error state is handled in the slices.
    }
  };

  const handlePreviewPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (zoom <= 1) {
      return;
    }
    event.preventDefault();
    updateUploadState({ isDragging: true });
    dragStartRef.current = {
      x: event.clientX - dragOffset.x,
      y: event.clientY - dragOffset.y,
    };
  };

  const buildNotificationPayload = (
    nextSettings: Record<NotificationKey, boolean>,
    nextEmailEnabled: boolean,
    nextAppEnabled: boolean
  ) => ({
    email_enabled: nextEmailEnabled,
    email_match_alert: nextSettings.emailMatchAlert,
    email_upcoming_session_reminder: nextSettings.emailUpcomingSessionReminder,
    email_session_cancellation: nextSettings.emailSessionCancellation,
    email_rescheduling_request: nextSettings.emailReschedulingRequest,
    email_platform_announcement: nextSettings.emailPlatformAnnouncement,
    email_new_message: nextSettings.emailNewMessage,
    in_app_enabled: nextAppEnabled,
    in_app_upcoming_session_reminder: nextSettings.inAppUpcomingSessionReminder,
    in_app_session_changes: nextSettings.inAppSessionChanges,
    in_app_new_student_assigned: nextSettings.inAppNewStudentAssigned,
    in_app_new_message: nextSettings.inAppNewMessage,
    in_app_action_required: nextSettings.inAppActionRequired,
    in_app_attendance_update: nextSettings.inAppAttendanceUpdate,
    in_app_platform_update: nextSettings.inAppPlatformUpdate,
  });

  const syncNotificationSettings = (
    nextSettings: Record<NotificationKey, boolean>,
    nextEmailEnabled: boolean,
    nextAppEnabled: boolean
  ) => {
    dispatch(
      updateNotificationSettings(
        buildNotificationPayload(nextSettings, nextEmailEnabled, nextAppEnabled)
      )
    );
  };

  const handleIntegrationManage = async (key: IntegrationKey) => {
    if (key === 'googleCalendar' && !isGoogleAuthLoading) {
      dispatch(resetGoogleIntegrationAuth());
      try {
        const { url } = await dispatch(getGoogleIntegrationAuthUrl()).unwrap();
        if (url) {
          window.location.assign(url);
        }
      } catch {
        // Error state is handled in the slice.
      }
    } else if (key === 'outlookCalendar' && !microsoftAuthLoading) {
      setMicrosoftAuthLoading(true);
      setMicrosoftAuthError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8082';
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const needsPrefix = !baseUrl.includes('localhost') && !baseUrl.includes('127.0.0.1') && !baseUrl.includes('/coaching/v1');
        const finalUrl = needsPrefix ? `${baseUrl}/coaching/v1` : baseUrl;
        const redirectUrl = `${window.location.origin}/settings?tab=integrations`;
        const authUrl = `${finalUrl}/api/integrations/microsoft/auth?redirect_url=${encodeURIComponent(redirectUrl)}`;
        
        const response = await fetch(authUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to get auth URL for Microsoft');
        }

        const data = await response.json();
        if (data.url) {
          window.location.assign(data.url);
        }
      } catch (err) {
        setMicrosoftAuthError(err instanceof Error ? err.message : 'Failed to connect to Outlook');
      } finally {
        setMicrosoftAuthLoading(false);
      }
    } else if (key === 'zoomCalendar' && !zoomAuthLoading) {
      setZoomAuthLoading(true);
      setZoomAuthError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8082';
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const needsPrefix =
          !baseUrl.includes('localhost') &&
          !baseUrl.includes('127.0.0.1') &&
          !baseUrl.includes('/coaching/v1');
        const finalUrl = needsPrefix ? `${baseUrl}/coaching/v1` : baseUrl;
        const redirectUrl = `${window.location.origin}/settings?tab=integrations`;
        const authUrl = `${finalUrl}/api/integrations/zoom/auth?redirect_url=${encodeURIComponent(
          redirectUrl
        )}`;

        const response = await fetch(authUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to get auth URL for Zoom');
        }

        const data = await response.json();
        if (data.url) {
          window.location.assign(data.url);
        }
      } catch (err) {
        setZoomAuthError(err instanceof Error ? err.message : 'Failed to connect to Zoom');
      } finally {
        setZoomAuthLoading(false);
      }
    }
  };

  const handleFileSelected = (file?: File | null) => {
    if (!file) {
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      updateUploadState({
        error: 'Only jpg, jpeg, png, or gif files are allowed.',
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      updateUploadState({ error: 'File size must be smaller than 2MB.' });
      return;
    }
    updateUploadState({
      error: '',
      file,
      dragOffset: { x: 0, y: 0 },
      baseImageSize: { width: 0, height: 0 },
    });
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    dispatch(getNotificationSettings());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab !== 'Account') {
      return;
    }
    dispatch(getAccountSessions());
  }, [activeTab, dispatch]);

  useEffect(() => {
    if (activeTab !== 'Integrations') {
      return;
    }
    dispatch(getIntegrations());
  }, [activeTab, dispatch]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (!requestedTab) {
      return;
    }
    const normalized = getTabSlug(requestedTab);
    const matched = SETTINGS_TABS.find((tab) => getTabSlug(tab) === normalized);
    if (matched && matched !== activeTab) {
      setActiveTab(matched);
    }
  }, [activeTab, searchParams]);

  useEffect(() => {
    if (!notificationSettingsData) {
      return;
    }
    updateNotificationState({
      emailEnabled: notificationSettingsData.email_enabled,
      appEnabled: notificationSettingsData.in_app_enabled,
      settings: {
        emailMatchAlert: notificationSettingsData.email_match_alert,
        emailUpcomingSessionReminder:
          notificationSettingsData.email_upcoming_session_reminder,
        emailSessionCancellation:
          notificationSettingsData.email_session_cancellation,
        emailReschedulingRequest:
          notificationSettingsData.email_rescheduling_request,
        emailPlatformAnnouncement:
          notificationSettingsData.email_platform_announcement,
        emailNewMessage: notificationSettingsData.email_new_message,
        inAppUpcomingSessionReminder:
          notificationSettingsData.in_app_upcoming_session_reminder,
        inAppSessionChanges: notificationSettingsData.in_app_session_changes,
        inAppNewStudentAssigned:
          notificationSettingsData.in_app_new_student_assigned,
        inAppNewMessage: notificationSettingsData.in_app_new_message,
        inAppActionRequired: notificationSettingsData.in_app_action_required,
        inAppAttendanceUpdate: notificationSettingsData.in_app_attendance_update,
        inAppPlatformUpdate: notificationSettingsData.in_app_platform_update,
      },
    });
  }, [notificationSettingsData]);

  useEffect(() => {
    if (!integrationsData) {
      return;
    }
    const nextSettings: Record<IntegrationKey, boolean> = {
      googleCalendar: false,
      outlookCalendar: false,
      zoomCalendar: false,
      appleCalendar: false,
    };
    integrationsData.forEach((integration) => {
      const provider = (integration?.provider || '').toLowerCase();
      if (!integration?.is_active) {
        return;
      }
      if (provider === 'google') {
        nextSettings.googleCalendar = true;
      } else if (provider === 'microsoft' || provider === 'outlook') {
        nextSettings.outlookCalendar = true;
      } else if (provider === 'zoom') {
        nextSettings.zoomCalendar = true;
      } else if (provider === 'apple') {
        nextSettings.appleCalendar = true;
      }
    });
    updateIntegrationState({ settings: nextSettings });
  }, [integrationsData]);

  useEffect(() => {
    if (hasNotificationSettingsError && notificationSettingsErrorMessage) {
      updateNotificationState({ snackbarOpen: true });
    }
  }, [hasNotificationSettingsError, notificationSettingsErrorMessage]);

  useEffect(() => {
    if (!uploadFile) {
      updateUploadState({ previewUrl: '' });
      return;
    }
    const nextUrl = URL.createObjectURL(uploadFile);
    updateUploadState({ previewUrl: nextUrl });
    return () => URL.revokeObjectURL(nextUrl);
  }, [uploadFile]);

  useEffect(() => {
    if (!isUploadOpen) {
      updateUploadState({
        file: null,
        error: '',
        zoom: 1,
        dragOffset: { x: 0, y: 0 },
        baseImageSize: { width: 0, height: 0 },
      });
    }
  }, [isUploadOpen]);

  const openPasswordModal = () => {
    updatePasswordState({
      isOpen: true,
      step: 'current',
      password: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    });
  };

  const closePasswordModal = () => {
    dispatch(resetChangePassword());
    updatePasswordState({
      isOpen: false,
      step: 'current',
      password: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    });
  };

  const handleFinishPasswordReset = async () => {
    if (!canProceedReset) {
      return;
    }
    try {
      await dispatch(
        changePassword({
          oldPassword: passwordDraft,
          newPassword: newPasswordDraft,
        })
      ).unwrap();
      closePasswordModal();
      // Optionally show success message
    } catch (error) {
      // Error state is handled in the slice
      console.error('Failed to change password:', error);
    }
  };

  const hasMinLength = newPasswordDraft.length >= 8;
  const hasLowercase = /[a-z]/.test(newPasswordDraft);
  const hasUppercase = /[A-Z]/.test(newPasswordDraft);
  const hasNumber = /[0-9]/.test(newPasswordDraft);
  const hasSpecial = /[!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]/.test(
    newPasswordDraft
  );
  const isPasswordValid =
    hasMinLength && hasLowercase && hasUppercase && hasNumber && hasSpecial;
  const isConfirmMatch =
    newPasswordDraft.length > 0 && newPasswordDraft === confirmPasswordDraft;
  const canProceedReset = isPasswordValid && isConfirmMatch;

  const handleEditName = () => {
    dispatch(resetUpdateCoachProfile());
    const { firstName: nextFirst, lastName: nextLast } = splitFullName(coachName);
    updateProfileState({
      firstNameDraft: nextFirst,
      lastNameDraft: nextLast,
      isEditingName: true,
    });
  };

  const handleSaveName = async () => {
    if (isProfileUpdating) {
      return;
    }
    const nextFirstName = firstNameDraft.trim();
    const nextLastName = lastNameDraft.trim();
    if (!nextFirstName || !nextLastName) {
      return;
    }
    const nextFullName = `${nextFirstName} ${nextLastName}`.trim();
    try {
      await dispatch(updateCoachProfile({ name: nextFullName })).unwrap();
      updateProfileState({
        firstNameDraft: nextFirstName,
        lastNameDraft: nextLastName,
        isEditingName: false,
      });
      dispatch(getCoachInfo());
    } catch {
      // Error state is handled in the slice.
    }
  };

  const handleCancelName = () => {
    dispatch(resetUpdateCoachProfile());
    const { firstName: nextFirst, lastName: nextLast } = splitFullName(coachName);
    updateProfileState({
      firstNameDraft: nextFirst,
      lastNameDraft: nextLast,
      isEditingName: false,
    });
  };

  const handleEditBio = () => {
    dispatch(resetUpdateCoachProfile());
    updateProfileState({ bioDraft: coachBio, isEditingBio: true });
  };

  const handleSaveBio = async () => {
    if (isProfileUpdating) {
      return;
    }
    const nextBio = bioDraft.trim();
    if (!nextBio) {
      return;
    }
    try {
      await dispatch(updateCoachProfile({ bio: nextBio })).unwrap();
      updateProfileState({ bioDraft: nextBio, isEditingBio: false });
      dispatch(getCoachInfo());
    } catch {
      // Error state is handled in the slice.
    }
  };

  const handleEditLocation = () => {
    dispatch(resetUpdateCoachProfile());
    updateProfileState({
      locationDraft: coachLocation,
      isEditingLocation: true,
    });
  };

  const handleSaveLocation = async () => {
    if (isProfileUpdating) {
      return;
    }
    const nextLocation = locationDraft.trim();
    if (!nextLocation) {
      return;
    }
    try {
      await dispatch(updateCoachProfile({ location: nextLocation })).unwrap();
      updateProfileState({
        locationDraft: nextLocation,
        isEditingLocation: false,
      });
      dispatch(getCoachInfo());
    } catch {
      // Error state is handled in the slice.
    }
  };

  const handleCancelLocation = () => {
    dispatch(resetUpdateCoachProfile());
    updateProfileState({
      locationDraft: coachLocation,
      isEditingLocation: false,
    });
  };

  const handleCancelBio = () => {
    dispatch(resetUpdateCoachProfile());
    updateProfileState({ bioDraft: coachBio, isEditingBio: false });
  };

  const handleToggleNotification = (key: NotificationKey) => {
    setNotificationState((prev) => {
      const next = { ...prev.settings, [key]: !prev.settings[key] };
      syncNotificationSettings(
        next,
        prev.emailEnabled,
        prev.appEnabled
      );
      return { ...prev, settings: next };
    });
  };

  const handleToggleEmailNotifications = () => {
    updateNotificationState({
      confirmToggle: {
        type: 'email',
        nextValue: !emailNotificationsEnabled,
      },
    });
  };

  const handleToggleAppNotifications = () => {
    updateNotificationState({
      confirmToggle: {
        type: 'app',
        nextValue: !appNotificationsEnabled,
      },
    });
  };

  const handleToggleIntegration = (key: IntegrationKey) => {
    updateIntegrationState({
      confirmToggle: {
        key,
        nextValue: !integrationSettings[key],
      },
    });
  };

  const handleConfirmToggle = () => {
    if (!confirmToggle) {
      return;
    }
    if (confirmToggle.type === 'email') {
      updateNotificationState({
        emailEnabled: confirmToggle.nextValue,
        confirmToggle: null,
      });
      syncNotificationSettings(
        notificationSettings,
        confirmToggle.nextValue,
        appNotificationsEnabled
      );
    } else {
      updateNotificationState({
        appEnabled: confirmToggle.nextValue,
        confirmToggle: null,
      });
      syncNotificationSettings(
        notificationSettings,
        emailNotificationsEnabled,
        confirmToggle.nextValue
      );
    }
  };

  const handleConfirmIntegration = async () => {
    if (!confirmIntegrationToggle) {
      return;
    }
    const { key, nextValue } = confirmIntegrationToggle;
    if (nextValue && key === 'googleCalendar') {
      try {
        dispatch(resetGoogleIntegrationAuth());
        const { url } = await dispatch(getGoogleIntegrationAuthUrl()).unwrap();
        if (url) {
          updateIntegrationState({
            settings: { ...integrationSettings, [key]: true },
          });
          window.location.assign(url);
        }
      } catch {
        // Error state is handled in the slice.
      } finally {
        updateIntegrationState({ confirmToggle: null });
      }
    } else if (nextValue && key === 'outlookCalendar') {
      setMicrosoftAuthLoading(true);
      setMicrosoftAuthError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8082';
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const needsPrefix = !baseUrl.includes('localhost') && !baseUrl.includes('127.0.0.1') && !baseUrl.includes('/coaching/v1');
        const finalUrl = needsPrefix ? `${baseUrl}/coaching/v1` : baseUrl;
        const redirectUrl = `${window.location.origin}/settings?tab=integrations`;
        const authUrl = `${finalUrl}/api/integrations/microsoft/auth?redirect_url=${encodeURIComponent(redirectUrl)}`;
        const response = await fetch(authUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to get auth URL for Microsoft');
        }

        const data = await response.json();
        if (data.url) {
          updateIntegrationState({
            settings: { ...integrationSettings, [key]: true },
          });
          window.location.assign(data.url);
        }
      } catch (err) {
        setMicrosoftAuthError(err instanceof Error ? err.message : 'Failed to connect to Outlook');
      } finally {
        setMicrosoftAuthLoading(false);
        updateIntegrationState({ confirmToggle: null });
      }
    } else if (nextValue && key === 'zoomCalendar') {
      setZoomAuthLoading(true);
      setZoomAuthError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8082';
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const needsPrefix =
          !baseUrl.includes('localhost') &&
          !baseUrl.includes('127.0.0.1') &&
          !baseUrl.includes('/coaching/v1');
        const finalUrl = needsPrefix ? `${baseUrl}/coaching/v1` : baseUrl;
        const redirectUrl = `${window.location.origin}/settings?tab=integrations`;
        const authUrl = `${finalUrl}/api/integrations/zoom/auth?redirect_url=${encodeURIComponent(
          redirectUrl
        )}`;
        const response = await fetch(authUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to get auth URL for Zoom');
        }

        const data = await response.json();
        if (data.url) {
          updateIntegrationState({
            settings: { ...integrationSettings, [key]: true },
          });
          window.location.assign(data.url);
        }
      } catch (err) {
        setZoomAuthError(err instanceof Error ? err.message : 'Failed to connect to Zoom');
      } finally {
        setZoomAuthLoading(false);
        updateIntegrationState({ confirmToggle: null });
      }
    } else {
      const providerMap: Record<IntegrationKey, string> = {
        googleCalendar: 'google',
        outlookCalendar: 'microsoft',
        zoomCalendar: 'zoom',
        appleCalendar: 'apple',
      };
      const provider = providerMap[key];
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8082';
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const needsPrefix =
          !baseUrl.includes('localhost') &&
          !baseUrl.includes('127.0.0.1') &&
          !baseUrl.includes('/coaching/v1');
        const finalUrl = needsPrefix ? `${baseUrl}/coaching/v1` : baseUrl;
        const disconnectUrl = `${finalUrl}/api/integrations/${provider}`;
        const response = await fetch(disconnectUrl, {
          method: 'DELETE',
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to disconnect integration');
        }

        updateIntegrationState({
          settings: { ...integrationSettings, [key]: false },
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to disconnect integration';
        if (key === 'outlookCalendar') {
          setMicrosoftAuthError(message);
        } else if (key === 'zoomCalendar') {
          setZoomAuthError(message);
        }
      } finally {
        updateIntegrationState({ confirmToggle: null });
      }
    }
  };

  const handleLogoutAllSessions = () => {
    dispatch(deleteAllAccountSessions()).finally(() => {
      // Clear Redux state
      dispatch(resetSignIn());
      dispatch(resetUpdateCoachProfile());
      dispatch(resetProfileImageUpload());
      dispatch(resetGoogleIntegrationAuth());
      dispatch(resetIntegrations());
      dispatch(resetLocationSearch());
      dispatch(resetSupportTicket());
      dispatch(resetAccountSessions());

      // Clear all auth data and redirect to login
      redirectToLogin();
    });
  };

  const handleLogoutSession = (sessionId: string) => {
    if (!sessionId) {
      return;
    }
    dispatch(deleteAccountSession({ sessionId }));
  };

  const handleOpenSupportQuery = () => {
    dispatch(resetSupportTicket());
    updateSupportState({ isSuccessOpen: false, isQueryOpen: true });
  };

  const handleCancelSupportQuery = () => {
    dispatch(resetSupportTicket());
    updateSupportState({
      isSuccessOpen: false,
      isQueryOpen: false,
      category: supportCategories[0],
      query: '',
    });
  };

  const handleSubmitSupportQuery = async () => {
    if (isSupportTicketLoading) {
      return;
    }
    const message = supportQuery.trim();
    if (!message) {
      return;
    }
    try {
      await dispatch(
        createSupportTicket({
          category: supportCategory,
          message,
        })
      ).unwrap();
      updateSupportState({
        isQueryOpen: false,
        isSuccessOpen: true,
        category: supportCategories[0],
        query: '',
      });
      dispatch(resetSupportTicket());
    } catch {
      // Error state is handled in the slice.
    }
  };

  const handleToggleFaq = (index: number) => {
    setSupportState((prev) => ({
      ...prev,
      openFaqIndex: prev.openFaqIndex === index ? null : index,
    }));
  };

  const handleCopyEmail = async () => {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        return;
      }
      await navigator.clipboard.writeText(supportEmail);
      updateSupportState({ isEmailCopied: true });
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        updateSupportState({ isEmailCopied: false });
      }, 1500);
    } catch {
      // Ignore clipboard errors.
    }
  };

  const clampOffset = useCallback(
    (offset: { x: number; y: number }, zoomValue: number) => {
      const container = previewContainerRef.current;
      if (!container || !baseImageSize.width || !baseImageSize.height) {
        return offset;
      }
      const containerRect = container.getBoundingClientRect();
      const scaledWidth = baseImageSize.width * zoomValue;
      const scaledHeight = baseImageSize.height * zoomValue;
      const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, offset.x)),
        y: Math.min(maxY, Math.max(-maxY, offset.y)),
      };
    },
    [baseImageSize.height, baseImageSize.width]
  );

  useEffect(() => {
    if (!isEditingLocation) {
      dispatch(resetLocationSearch());
      return;
    }

    const query = locationDraft.trim();
    if (!query) {
      dispatch(resetLocationSearch());
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(getLocationSearchResults(query));
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [dispatch, isEditingLocation, locationDraft]);

  useEffect(() => {
    if (zoom <= 1) {
      updateUploadState({
        dragOffset: { x: 0, y: 0 },
        isDragging: false,
      });
      return;
    }
    setUploadState((prev) => ({
      ...prev,
      dragOffset: clampOffset(prev.dragOffset, zoom),
    }));
  }, [zoom, clampOffset]);

  useEffect(() => {
    if (!isDraggingImage) {
      return;
    }
    const handleMove = (event: globalThis.PointerEvent) => {
      const nextOffset = {
        x: event.clientX - dragStartRef.current.x,
        y: event.clientY - dragStartRef.current.y,
      };
      setUploadState((prev) => ({
        ...prev,
        dragOffset: clampOffset(nextOffset, zoom),
      }));
    };
    const handleUp = () => {
      updateUploadState({ isDragging: false });
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [clampOffset, isDraggingImage, zoom]);

  const handleZoomWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!previewUrl) {
      return;
    }
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.08 : -0.08;
    setUploadState((prev) => ({
      ...prev,
      zoom: Math.min(2, Math.max(1, prev.zoom + delta)),
    }));
  };

  const updateBaseImageSize = () => {
    const container = previewContainerRef.current;
    const image = previewImageRef.current;
    if (!container || !image || !image.naturalWidth || !image.naturalHeight) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const scale = Math.min(
      1,
      containerRect.width / image.naturalWidth,
      containerRect.height / image.naturalHeight
    );
    setUploadState((prev) => ({
      ...prev,
      baseImageSize: {
        width: image.naturalWidth * scale,
        height: image.naturalHeight * scale,
      },
      dragOffset: clampOffset(prev.dragOffset, zoom),
    }));
  };

  const profileErrorText = hasProfileError ? profileErrorMessage : '';
  const profileSectionData = {
    fullName: fullNameDisplay,
    avatarUrl: coachAvatarUrl,
    avatarInitials,
    profileRows,
    location: locationDisplay,
    locationDraft,
    locationResults,
    isLocationSearching,
    isEditingLocation,
    isEditingName,
    firstNameDraft,
    lastNameDraft,
    isSavingProfile: isProfileUpdating,
    bio: bioDisplay,
    bioDraft,
    isEditingBio,
    profileErrorMessage: profileErrorText,
  };
  const profileSectionActions = {
    onUpload: openUpload,
    onLocationChange: (value: string) =>
      updateProfileState({ locationDraft: value }),
    onEditLocation: handleEditLocation,
    onSaveLocation: handleSaveLocation,
    onCancelLocation: handleCancelLocation,
    onFirstNameChange: (value: string) =>
      updateProfileState({ firstNameDraft: value }),
    onLastNameChange: (value: string) =>
      updateProfileState({ lastNameDraft: value }),
    onEditName: handleEditName,
    onSaveName: handleSaveName,
    onCancelName: handleCancelName,
    onBioChange: (value: string) => updateProfileState({ bioDraft: value }),
    onEditBio: handleEditBio,
    onSaveBio: handleSaveBio,
    onCancelBio: handleCancelBio,
  };
  const accountSectionData = {
    registeredEmail: registeredEmailDisplay,
    sessions: accountSessionsData ?? [],
    isSessionsLoading: isAccountSessionsLoading,
    deletingSessionIds: accountSessionDeletingIds,
    isLogoutAllLoading: isAccountSessionsDeletingAll,
  };
  const accountSectionActions = {
    onChangePassword: openPasswordModal,
    onLogoutAll: handleLogoutAllSessions,
    onLogoutSession: handleLogoutSession,
  };
  const notificationsSectionData = {
    settings: notificationSettings,
    emailEnabled: emailNotificationsEnabled,
    appEnabled: appNotificationsEnabled,
    isBusy: isNotificationSettingsLoading || isNotificationSettingsSaving,
    emailOptions: emailNotificationOptions,
    appOptions: appNotificationOptions,
  };
  const notificationsSectionActions = {
    onToggle: handleToggleNotification,
    onToggleEmail: handleToggleEmailNotifications,
    onToggleApp: handleToggleAppNotifications,
  };
  const filteredIntegrationOptions = integrationOptions.filter(
    (option) => option.key === 'googleCalendar' || option.key === 'outlookCalendar' || option.key === 'zoomCalendar'
  );
  const integrationsSectionData = {
    settings: integrationSettings,
    options: filteredIntegrationOptions,
    isGoogleAuthLoading,
    googleAuthErrorMessage: hasGoogleAuthError ? googleAuthErrorMessage : '',
    isMicrosoftAuthLoading: microsoftAuthLoading,
    microsoftAuthErrorMessage: microsoftAuthError || '',
    isZoomAuthLoading,
    zoomAuthErrorMessage,
  };
  const integrationsSectionActions = {
    onToggle: handleToggleIntegration,
    onManage: handleIntegrationManage,
  };
  const helpSupportSectionData = {
    isQueryOpen: isSupportQueryOpen,
    category: supportCategory,
    query: supportQuery,
    isSubmitting: isSupportTicketLoading,
    errorMessage: hasSupportTicketError ? supportTicketErrorMessage : '',
    supportEmail,
    categoryOptions: supportCategoryOptions,
    faqs: supportFaqs,
    openFaqIndex,
    isEmailCopied,
  };
  const helpSupportSectionActions = {
    onOpenQuery: handleOpenSupportQuery,
    onCategoryChange: (value: string) =>
      updateSupportState({ category: value }),
    onQueryChange: (value: string) => updateSupportState({ query: value }),
    onCancelQuery: handleCancelSupportQuery,
    onSubmitQuery: handleSubmitSupportQuery,
    onToggleFaq: handleToggleFaq,
    onCopyEmail: handleCopyEmail,
  };
  const settingsOverlaysData = {
    confirmToggle,
    confirmIntegrationToggle,
    isSupportSuccessOpen,
    snackbarOpen,
    snackbarMessage:
      notificationSettingsErrorMessage || 'Failed to update notifications.',
  };
  const settingsOverlaysActions = {
    onCloseConfirmToggle: () => updateNotificationState({ confirmToggle: null }),
    onConfirmToggle: handleConfirmToggle,
    onCloseConfirmIntegration: () =>
      updateIntegrationState({ confirmToggle: null }),
    onConfirmIntegration: handleConfirmIntegration,
    onCloseSupportSuccess: () => updateSupportState({ isSuccessOpen: false }),
    onCloseSnackbar: () => updateNotificationState({ snackbarOpen: false }),
  };
  const uploadModalData = {
    isOpen: isUploadOpen,
    previewUrl,
    zoom,
    isDragging: isDraggingImage,
    dragOffset,
    uploadError,
    isSaving: isProfileImageUploading || isProfileUpdating,
    canSave: Boolean(uploadFile),
    hasError: hasProfileImageUploadError || hasProfileError,
    errorMessage: hasProfileImageUploadError
      ? profileImageUploadErrorMessage
      : profileErrorMessage,
  };
  const uploadModalRefs = {
    fileInputRef,
    previewContainerRef,
    previewImageRef,
  };
  const uploadModalActions = {
    onClose: closeUpload,
    onSave: handleSavePhoto,
    onChooseFile: handleChooseFile,
    onFileSelected: handleFileSelected,
    onZoomChange: (value: number) => updateUploadState({ zoom: value }),
    onZoomIn: () =>
      setUploadState((prev) => ({
        ...prev,
        zoom: Math.min(2, prev.zoom + 0.1),
      })),
    onZoomOut: () =>
      setUploadState((prev) => ({
        ...prev,
        zoom: Math.max(1, prev.zoom - 0.1),
      })),
    onWheel: handleZoomWheel,
    onPointerDown: handlePreviewPointerDown,
    onImageLoad: updateBaseImageSize,
  };
  const passwordModalData = {
    isOpen: isPasswordModalOpen,
    step: passwordStep,
    password: passwordDraft,
    newPassword: newPasswordDraft,
    confirmPassword: confirmPasswordDraft,
    showPassword,
    showNewPassword,
    showConfirmPassword,
    canProceedCurrent: Boolean(passwordDraft.trim()),
    canProceedReset,
    hasMinLength,
    hasLowercase,
    hasUppercase,
    hasNumber,
    hasSpecial,
    showRequirements: newPasswordDraft.length > 0,
    isLoading: isPasswordChanging,
    errorMessage: passwordChangeErrorMessage,
  };
  const passwordModalActions = {
    onClose: closePasswordModal,
    onNextFromCurrent: () => updatePasswordState({ step: 'reset' }),
    onFinishReset: handleFinishPasswordReset,
    onPasswordChange: (value: string) =>
      updatePasswordState({ password: value }),
    onNewPasswordChange: (value: string) =>
      updatePasswordState({ newPassword: value }),
    onConfirmPasswordChange: (value: string) =>
      updatePasswordState({ confirmPassword: value }),
    onToggleShowPassword: () =>
      setPasswordState((prev) => ({
        ...prev,
        showPassword: !prev.showPassword,
      })),
    onToggleShowNewPassword: () =>
      setPasswordState((prev) => ({
        ...prev,
        showNewPassword: !prev.showNewPassword,
      })),
    onToggleShowConfirmPassword: () =>
      setPasswordState((prev) => ({
        ...prev,
        showConfirmPassword: !prev.showConfirmPassword,
      })),
  };

  return (
    <>
      <div className="px-6 lg:px-[100px]">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-4">
            {SETTINGS_TABS.map((tab) => (
              <TabButton
                key={tab}
                label={tab}
                active={activeTab === tab}
                onClick={() => handleTabChange(tab)}
              />
            ))}
          </div>
          {activeTab === 'Profile' ? (
            <ProfileSection
              data={profileSectionData}
              actions={profileSectionActions}
            />
          ) : activeTab === 'Account' ? (
            <AccountSection
              data={accountSectionData}
              actions={accountSectionActions}
            />
          ) : activeTab === 'Notifications & Alerts' ? (
            <NotificationsSection
              data={notificationsSectionData}
              actions={notificationsSectionActions}
            />
          ) : activeTab === 'Integrations' ? (
            <IntegrationsSection
              data={integrationsSectionData}
              actions={integrationsSectionActions}
            />
          ) : activeTab === 'Help & Support' ? (
            <HelpSupportSection
              data={helpSupportSectionData}
              actions={helpSupportSectionActions}
            />
          ) : (
            <PlaceholderSection title={activeTab} />
          )}
        </div>
      </div>


      <SettingsOverlays
        data={settingsOverlaysData}
        actions={settingsOverlaysActions}
      />
      <UploadModal
        data={uploadModalData}
        refs={uploadModalRefs}
        actions={uploadModalActions}
      />
      <PasswordModal
        data={passwordModalData}
        actions={passwordModalActions}
      />

    </>
  );
};

export default layout(Settings);
